import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/services/supabase";
import googleIcon from "@/assets/google.svg";
import { AuthLayout, AuthLegalFooter } from "@/features/auth/components/AuthLayout";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps extends React.ComponentProps<"div"> {
  onSubmit?: (e: React.FormEvent) => void;
}

const authPrimaryBtn =
  "w-full motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-panel";

export function LoginForm({ className, onSubmit, ...props }: LoginFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    setError(null);

    try {
      const { data: signInData, error } = await supabase.auth.signInWithPassword(
        {
          email: values.email,
          password: values.password,
        }
      );

      if (error) throw error;

      const userId = signInData.user?.id;
      if (userId) {
        const { data: companyUser, error: companyUserError } = await supabase
          .from("company_users")
          .select("active")
          .eq("id", userId)
          .maybeSingle();

        if (companyUserError) {
          await supabase.auth.signOut();
          throw new Error("Unable to verify account status. Please try again.");
        }

        if (companyUser && companyUser.active === false) {
          await supabase.auth.signOut();
          throw new Error(
            "This account has been deactivated. Contact your administrator."
          );
        }
      }

      if (onSubmit) {
        const syntheticEvent = new Event(
          "submit"
        ) as unknown as React.FormEvent;
        onSubmit(syntheticEvent);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred during login"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(false);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetSuccess(true);
    } catch (err) {
      setResetError(
        err instanceof Error
          ? err.message
          : "An error occurred sending the reset email"
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setResetEmail("");
    setResetError(null);
    setResetSuccess(false);
  };

  return (
    <div className={cn(className)} {...props}>
      <AuthLayout
        title="Sign in"
        description="Access your portfolio workspace."
        footer={<AuthLegalFooter />}
      >
        <Form {...form}>
          <form
            className="flex flex-col gap-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {error && (
              <div className="rounded-sm bg-destructive px-3 py-2.5 text-sm text-destructive-foreground">
                {error}
              </div>
            )}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      autoComplete="email"
                      data-testid="login-email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center">
                    <FormLabel>Password</FormLabel>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="ml-auto text-sm text-exec underline-offset-2 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        data-testid="login-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-current" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-current" />
                        )}
                        <span className="sr-only">
                          {showPassword ? "Hide password" : "Show password"}
                        </span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {props.children || (
              <Button
                type="submit"
                className={authPrimaryBtn}
                disabled={loading}
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            )}
            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-tide">
              <span className="relative z-10 bg-surface-solid px-2 text-slate-current">
                Or continue with
              </span>
            </div>
            <Button
              variant="outline"
              className="w-full gap-2"
              type="button"
            >
              <img src={googleIcon} alt="" className="h-5 w-5" />
              <span>Sign in with Google</span>
            </Button>
            <p className="text-center text-sm text-slate-current">
              Don&apos;t have an account?{" "}
              <Link
                to="/register"
                className="font-medium text-exec underline-offset-4 hover:underline"
              >
                Sign up
              </Link>
            </p>
          </form>
        </Form>
      </AuthLayout>

      <Dialog
        open={showForgotPassword}
        onOpenChange={(open) => {
          if (!open) handleCloseForgotPassword();
        }}
      >
        <DialogContent className="border-tide bg-surface-solid sm:rounded-md">
          <DialogHeader>
            <p className="metadata-label">Account</p>
            <DialogTitle className="tracking-tightest">
              Reset your password
            </DialogTitle>
            <DialogDescription className="text-slate-current">
              Enter your email and we&apos;ll send a link to choose a new
              password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword}>
            <div className="space-y-4 py-2">
              {resetSuccess ? (
                <div className="rounded-sm bg-mist px-3 py-2.5 text-sm text-financial-ink">
                  Password reset email sent. Check your inbox for instructions.
                </div>
              ) : (
                <>
                  {resetError && (
                    <div className="rounded-sm bg-destructive px-3 py-2.5 text-sm text-destructive-foreground">
                      {resetError}
                    </div>
                  )}
                  <div className="space-y-2">
                    <label
                      htmlFor="reset-email"
                      className="text-sm font-medium text-financial-ink"
                    >
                      Email
                    </label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="name@company.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      disabled={resetLoading}
                      autoComplete="email"
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              {resetSuccess ? (
                <Button
                  type="button"
                  className={authPrimaryBtn}
                  onClick={handleCloseForgotPassword}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseForgotPassword}
                    disabled={resetLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className={authPrimaryBtn}
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Sending…" : "Send reset link"}
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
